import CloseIcon from "@mui/icons-material/Close";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";

export interface PhonemeTextFieldHandle {
  handleAddPhonemeValue: (newValue: string) => void;
}

interface PhonemeTextFieldProps {
  userPhoneme: string;
  setPhoneme: React.Dispatch<React.SetStateAction<string>>;
}

const PhonemeTextField = forwardRef<
  PhonemeTextFieldHandle,
  PhonemeTextFieldProps
>(({ userPhoneme, setPhoneme }, ref) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [cursorPosition, setCursorPosition] = useState<number>(0);

  useImperativeHandle(ref, () => ({
    handleAddPhonemeValue: (newValue: string) => {
      setPhoneme((prev: string) => {
        const beforeCursor = prev.slice(0, cursorPosition);
        const afterCursor = prev.slice(cursorPosition);
        const updatedText = beforeCursor + newValue + afterCursor;
        setCursorPosition(cursorPosition + newValue.length);
        return updatedText;
      });
    },
  }));

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneme(e.target.value);
    setCursorPosition(e.target.selectionStart || 0);
  };

  const handleOnClick = () => {
    if (inputRef.current) {
      setCursorPosition(inputRef.current.selectionStart || 0);
    }
  };

  return (
    <TextField
      fullWidth
      value={userPhoneme}
      onChange={handleOnChange}
      onClick={handleOnClick}
      placeholder={t("tts.toolbarMenu.pronunciationMenu.phonemePlaceholder")}
      inputRef={inputRef}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => {
                setPhoneme("");
                setCursorPosition(0);
              }}
            >
              <CloseIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
});

export default PhonemeTextField;
